/**
 * "Scroll" hint: visible while its scroll area has more content and is not yet scrolled to the end.
 * Default: the mobile menu drawer (vertical). With data-axis="x" and data-scroller="<selector>" it
 * watches that element inside the closest [data-scroll-root] horizontally (e.g. swipeable tiles).
 * Rechecks on scroll, resize and when accordions open/close.
 */
class EbScrollHint extends HTMLElement {
  #abort = new AbortController();
  /** @type {ResizeObserver | null} */
  #resize = null;

  connectedCallback() {
    const horizontal = this.dataset.axis === 'x';
    const scroller = this.dataset.scroller
      ? this.closest('[data-scroll-root]')?.querySelector(this.dataset.scroller)
      : this.closest('.menu-drawer, details')?.querySelector('.menu-drawer__navigation');
    if (!(scroller instanceof HTMLElement)) return;
    const update = () => {
      const overflow = horizontal
        ? scroller.scrollWidth - scroller.clientWidth
        : scroller.scrollHeight - scroller.clientHeight;
      const position = horizontal ? Math.abs(scroller.scrollLeft) : scroller.scrollTop;
      const atEnd = position >= overflow - 24;
      this.toggleAttribute('data-visible', overflow > 12 && !atEnd);
    };
    const { signal } = this.#abort;
    scroller.addEventListener('scroll', update, { passive: true, signal });
    scroller.addEventListener('toggle', () => requestAnimationFrame(update), { capture: true, signal });
    this.#resize = new ResizeObserver(update);
    this.#resize.observe(scroller);
    const list = scroller.firstElementChild;
    if (list) this.#resize.observe(list);
    update();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
    this.#resize?.disconnect();
    this.#resize = null;
  }
}

if (!customElements.get('eb-scroll-hint')) customElements.define('eb-scroll-hint', EbScrollHint);
